/**
 * Tab 01 · State — the daily verdict plus one next step.
 *
 * One component, not three: the screen renders by maturity level rather than by
 * a hand-picked variant, so a profile that gains its first report simply grows
 * the blocks it earned. Anything that needs reports stays hidden until they exist.
 *
 * Mobile is a single column. Desktop is three: a 340px rail with the orb and the
 * week, a fluid middle with the insight and the eight directions, and a 320px
 * rail with today's tasks and the strongest blocker. The direction strip becomes
 * a 2×4 grid there — a horizontal scroller inside a flex column is what pushed
 * the page sideways in the first pass.
 */
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../i18n/LangContext';
import { formatDate } from '../i18n/detectLanguage';
import { useT, useTt } from '../i18n/useT';
import { AppShell, DeskRow, HeaderStatus, MobileHeader } from './AppShell';
import { useAppContent, AppContent, Exercise } from './content';
import { useProfile, Profile, DirState, PatState } from './profile';
import { Icon } from './icons';
import { Orb, Ring } from './orb';
import { XpTag } from './XpTag';
import {
    Appear,
    Body,
    Btn,
    Col,
    Fluid,
    Hint,
    Meta,
    Muted,
    Panel,
    Rail,
    Row,
    Sec,
    SegBarC,
    ZoneLevels,
    useIsDesktop,
} from './primitives';
import {
    c,
    hair,
    hair2,
    f,
    ACT,
    scoreColor,
    patternZone,
    effectiveStrength,
    strengthKey,
    DIR_ORDER,
    LOAD,
    DESKTOP,
    DAY_KEYS,
} from './tokens';
import { R, QUIZ } from './routes';
import { todayIndex } from './tasks';
import { DAY_GOAL } from './points';

const HOW_KEY = 'dys.app.howItWorks.dismissed';

/* ── shared pieces ─────────────────────────────────── */

/**
 * Base / dynamic split, stated openly so a moving number never looks arbitrary.
 * Dynamic and the level bar are ACT — points keep one colour everywhere; only
 * the measured score wears its zone.
 */
const Honesty: React.FC<{
    base: number | null;
    dynamic: number;
    level: number;
    /** 0–1 through the current level. */
    levelPct: number;
    testedAt: Date | null;
    style?: React.CSSProperties;
}> = ({ base, dynamic, level, levelPct, testedAt, style }) => {
    const { t, lang } = useTt();
    const empty = dynamic <= 0;

    return (
        <Col gap={11} style={style}>
            <Row style={{ border: hair, borderRadius: 14, overflow: 'hidden', alignItems: 'stretch' }}>
                <Col
                    style={{ flex: 1, minWidth: 0, padding: '11px 13px', background: 'rgba(232,230,224,0.015)' }}
                    gap={4}
                >
                    <Row style={{ gap: 7 }}>
                        <Meta style={{ fontSize: 9.5, letterSpacing: '0.12em' }}>{t('app.state.base')}</Meta>
                        <span style={{ fontFamily: f.mono, fontSize: 14, color: c.textPri, fontWeight: 300 }}>
                            {base ?? '–'}
                        </span>
                    </Row>
                    <span style={{ fontSize: 10, color: c.subtle, lineHeight: 1.3 }}>
                        {testedAt
                            ? t('app.state.base.from', {
                                  date: formatDate(lang, testedAt, { day: 'numeric', month: 'long' }),
                              })
                            : t('app.state.base.fromLast')}
                    </span>
                    {/* Lands on the directions list, not the acquisition funnel:
                        a retake is per direction, and choosing one is step one. */}
                    <Link to={R.dirs} style={{ textDecoration: 'none' }}>
                        <Row style={{ gap: 5, marginTop: 2 }}>
                            <Meta style={{ fontSize: 9, color: c.text, letterSpacing: '0.1em' }}>
                                {t('app.state.retake')}
                            </Meta>
                            <Icon k="arrow" size={11} color={c.text} />
                        </Row>
                    </Link>
                </Col>
                <Col style={{ flex: 1, minWidth: 0, padding: '11px 13px', borderLeft: hair }} gap={4}>
                    <Row style={{ gap: 7 }}>
                        <Meta style={{ fontSize: 9.5, letterSpacing: '0.12em' }}>{t('app.state.dynamic')}</Meta>
                        <span style={{ fontFamily: f.mono, fontSize: 14, color: empty ? c.dim : ACT, fontWeight: 300 }}>
                            {empty ? '0' : dynamic.toLocaleString(lang === 'uk' ? 'uk-UA' : 'en-US')}
                        </span>
                    </Row>
                    <span style={{ fontSize: 10, color: c.subtle, lineHeight: 1.3 }}>
                        {t(empty ? 'app.state.dynamic.empty' : 'app.state.dynamic.sum')}
                    </span>
                </Col>
            </Row>
            <Row style={{ gap: 7 }}>
                <Hint text={t('app.state.level.hint')}>
                    <Meta
                        style={{
                            fontSize: 9.5,
                            color: empty ? c.subtle : c.text,
                            letterSpacing: '0.1em',
                            borderBottom: '1px dotted rgba(232,230,224,0.3)',
                            cursor: 'help',
                        }}
                    >
                        {t('app.state.level', { level })}
                    </Meta>
                </Hint>
                <div style={{ flex: 1, height: 3, borderRadius: 2, background: c.track, overflow: 'hidden' }}>
                    <div
                        style={{
                            width: `${Math.round(levelPct * 100)}%`,
                            height: '100%',
                            background: ACT,
                            transition: 'width 420ms ease',
                        }}
                    />
                </div>
            </Row>
        </Col>
    );
};

/** Mobile: horizontal strip of all eight directions. Untaken ones show a lock. */
const DirStrip: React.FC<{ dirs: DirState[]; content: AppContent }> = ({ dirs, content }) => {
    const { t, tn } = useTt();

    return (
        <Row style={{ gap: 9, padding: '0 20px 4px', overflowX: 'auto', scrollbarWidth: 'none' }}>
        {DIR_ORDER.map((code) => {
            const d = dirs.find((x) => x.code === code);
            const deep = !!d?.deep;
            const z = scoreColor(deep ? d?.score : null);

            return (
                <Link key={code} to={R.dir(code)} style={{ textDecoration: 'none', flex: '0 0 auto' }}>
                    <Col
                        style={{
                            width: 88,
                            alignItems: 'center',
                            background: c.card,
                            border: hair,
                            borderRadius: 15,
                            padding: '12px 8px 11px',
                        }}
                        gap={8}
                    >
                        <Ring score={deep ? (d?.score ?? null) : null} size={44} sw={3.2} neutral={!deep}>
                            <Icon
                                k={deep ? code : 'lock'}
                                size={deep ? 17 : 14}
                                color={deep ? z.hex : 'rgba(232,230,224,0.3)'}
                            />
                        </Ring>
                        <span
                            style={{
                                fontSize: 11.5,
                                color: deep ? c.textPri : c.dim,
                                fontWeight: 400,
                                textAlign: 'center',
                            }}
                        >
                            {content.dirBy[code]?.disp || code}
                        </span>
                        <span
                            style={{
                                fontFamily: f.mono,
                                fontSize: 10.5,
                                color: deep ? c.muted : c.subtle,
                                letterSpacing: '0.06em',
                            }}
                        >
                            {deep ? d?.score : t('app.zone.locked')}
                        </span>
                        {!!d?.earned && (
                            <Meta style={{ fontSize: 8.5, color: ACT, letterSpacing: '0.08em' }}>
                                +{tn('app.state.points', d.earned)}
                            </Meta>
                        )}
                    </Col>
                </Link>
            );
        })}
        </Row>
    );
};

/** Desktop: the same eight directions as a 2×4 card grid — no sideways scroll. */
const DirGrid: React.FC<{ dirs: DirState[]; content: AppContent }> = ({ dirs, content }) => {
    const { t, tn } = useTt();

    return (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10 }}>
        {DIR_ORDER.map((code) => {
            const d = dirs.find((x) => x.code === code);
            const deep = !!d?.deep;
            const z = scoreColor(deep ? d?.score : null);

            return (
                <Link key={code} to={R.dir(code)} style={{ textDecoration: 'none', minWidth: 0 }}>
                    <Col
                        style={{
                            border: hair,
                            borderRadius: 14,
                            padding: '13px 14px',
                            background: deep ? c.cardIn : 'transparent',
                            minWidth: 0,
                        }}
                        gap={11}
                    >
                        <Row style={{ gap: 9 }}>
                            <Icon k={deep ? code : 'lock'} size={15} color={deep ? z.hex : 'rgba(232,230,224,0.3)'} />
                            <span
                                style={{
                                    fontSize: 13,
                                    color: deep ? c.textPri : c.dim,
                                    fontWeight: 400,
                                    flex: 1,
                                    minWidth: 0,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                }}
                            >
                                {content.dirBy[code]?.disp || code}
                            </span>
                            {deep && (
                                <span style={{ fontFamily: f.mono, fontSize: 14, fontWeight: 300, color: c.textPri }}>
                                    {d?.score}
                                </span>
                            )}
                        </Row>
                        {deep ? (
                            <SegBarC pct={d?.score || 0} hex={z.hex} n={10} />
                        ) : (
                            <div style={{ height: 5, borderRadius: 2, border: '1px dashed rgba(232,230,224,0.14)' }} />
                        )}
                        <Row style={{ justifyContent: 'space-between' }}>
                            <Meta style={{ fontSize: 8.5, letterSpacing: '0.1em', color: deep ? z.hex : c.subtle }}>
                                {t(deep ? z.key : 'app.zone.locked')}
                            </Meta>
                            {/* Always the same unit in this corner. A pattern count
                                next to another card's "+10" read as two currencies.
                                A LOCKED direction can still hold earned points — a
                                task's pattern may live here — and hiding them made
                                the visible sums disagree with Dynamic. */}
                            {d && d.earned > 0 ? (
                                <Meta style={{ fontSize: 8.5, color: ACT }}>
                                    +{tn('app.state.points', d.earned)}
                                </Meta>
                            ) : (
                                deep && (
                                    <Meta style={{ fontSize: 8.5, color: c.subtle }}>
                                        {tn('app.state.points', 0)}
                                    </Meta>
                                )
                            )}
                        </Row>
                    </Col>
                </Link>
            );
        })}
        </div>
    );
};

/**
 * Insight of the day. Personal only when there are reports to draw on; otherwise
 * it is explicitly a library card, because a generic sentence in a personal slot
 * is the fastest way to lose trust in every other number on the screen.
 */
const Insight: React.FC<{ content: AppContent; dirCode: string; fromLibrary: boolean; wide?: boolean }> = ({
    content,
    dirCode,
    fromLibrary,
    wide,
}) => {
    const t = useT();
    const d = content.dirBy[dirCode];
    const hex = '#f59e0b';

    if (!d) return null;

    const label = (
        <Row style={{ gap: 9, marginBottom: wide ? 16 : 10 }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: hex, boxShadow: `0 0 8px ${hex}` }} />
            <Meta style={{ color: hex, fontSize: 9.5, letterSpacing: '0.16em' }}>
                {t(fromLibrary ? 'app.state.insight.library' : 'app.state.insight')}
            </Meta>
        </Row>
    );

    const open = (
        <Link to={R.dir(d.code)} style={{ textDecoration: 'none' }}>
            <Row style={{ gap: 8 }}>
                <Meta style={{ color: hex, fontSize: 10, letterSpacing: '0.14em' }}>
                    {t('app.state.insight.open', { dir: d.disp.toUpperCase() })}
                </Meta>
                <Icon k="arrow" size={13} color={hex} />
            </Row>
        </Link>
    );

    return (
        <div
            style={{
                border: `1px solid ${hex}33`,
                background: `linear-gradient(${wide ? '140deg' : '180deg'}, ${hex}0f, ${c.card} ${wide ? '55%' : '62%'})`,
                borderRadius: wide ? 18 : 17,
                padding: wide ? '22px 24px' : '15px 16px',
                minWidth: 0,
            }}
        >
            {label}
            {wide ? (
                // Wraps rather than squeezing: around 1200px the middle column is
                // narrow enough that side-by-side turns the headline into a
                // one-word-per-line ribbon.
                <Row style={{ gap: 26, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                    <Col style={{ flex: '1 1 300px', minWidth: 0 }} gap={11}>
                        <div
                            style={{
                                fontFamily: f.serif,
                                fontWeight: 300,
                                fontSize: 26,
                                lineHeight: 1.2,
                                letterSpacing: '-0.02em',
                                color: c.textPri,
                            }}
                        >
                            {d.tag}
                        </div>
                        <Body style={{ fontSize: 14 }}>{d.insight}</Body>
                    </Col>
                    <Col style={{ flex: '1 1 230px', minWidth: 0 }} gap={9}>
                        <Muted style={{ fontSize: 13 }}>{d.opis}</Muted>
                        {open}
                    </Col>
                </Row>
            ) : (
                <>
                    <div
                        style={{
                            fontSize: 16,
                            fontWeight: 400,
                            color: c.textPri,
                            letterSpacing: '-0.018em',
                            lineHeight: 1.3,
                        }}
                    >
                        {d.tag}
                    </div>
                    <Muted style={{ marginTop: 8, fontSize: 13.5 }}>{d.insight}</Muted>
                    <div style={{ marginTop: 13 }}>{open}</div>
                </>
            )}
        </div>
    );
};

/** One-time explainer for the first days, dismissible and remembered. */
const HOW_STEPS = ['test', 'dirs', 'pats', 'tasks', 'progress'];

const HowItWorks: React.FC<{ onClose: () => void; bare?: boolean }> = ({ onClose, bare }) => {
    const t = useT();
    const inner = (
        <>
            {!bare && (
                <Row style={{ gap: 9 }}>
                    <Icon k="help" size={14} color={c.muted} />
                    <Meta style={{ fontSize: 10, letterSpacing: '0.14em' }}>{t('app.state.how')}</Meta>
                    <button
                        type="button"
                        aria-label={t('app.action.hide')}
                        onClick={onClose}
                        style={{
                            marginLeft: 'auto',
                            width: 24,
                            height: 24,
                            borderRadius: '50%',
                            border: hair,
                            background: 'transparent',
                            display: 'grid',
                            placeItems: 'center',
                            cursor: 'pointer',
                        }}
                    >
                        <Icon k="close" size={11} color={c.subtle} />
                    </button>
                </Row>
            )}
            <Col style={{ marginTop: bare ? 0 : 12 }} gap={10}>
                {HOW_STEPS.map((step, i) => (
                    <Row key={step} style={{ gap: 11, alignItems: 'flex-start' }}>
                        <Meta style={{ fontSize: 9.5, color: c.dim, marginTop: 2, flex: '0 0 auto' }}>
                            {String(i + 1).padStart(2, '0')}
                        </Meta>
                        <Col gap={3} style={{ minWidth: 0 }}>
                            <span style={{ fontSize: 13, color: c.textPri, fontWeight: 400 }}>
                                {t(`app.state.how.${step}.title`)}
                            </span>
                            <Muted style={{ fontSize: 12 }}>{t(`app.state.how.${step}.body`)}</Muted>
                        </Col>
                    </Row>
                ))}
            </Col>
            <Row style={{ gap: 9, marginTop: 14, paddingTop: 12, borderTop: hair2 }}>
                <button
                    type="button"
                    onClick={onClose}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 8,
                        flex: 1,
                        height: 36,
                        borderRadius: 99,
                        border: hair,
                        background: 'transparent',
                        color: c.text,
                        fontFamily: f.ui,
                        fontSize: 12.5,
                        cursor: 'pointer',
                    }}
                >
                    <Icon k="check" size={13} color={c.text} />
                    {t('app.state.how.dismiss')}
                </button>
            </Row>
        </>
    );

    if (bare) return inner;
    return (
        <div
            style={{ border: hair, borderRadius: 16, padding: '15px 16px 14px', background: 'rgba(232,230,224,0.02)' }}
        >
            {inner}
        </div>
    );
};

/** Suggested exercise. Not a started task — the Tasks tab owns that state. */
const SuggestionRow: React.FC<{
    ex: Exercise;
    patternName: string;
    content: AppContent;
    scoreOf: (code: string) => number | null;
    accent?: string;
    compact?: boolean;
}> = ({ ex, patternName, content, scoreOf, accent, compact }) => {
    const t = useT();

    return (
        <Link to={R.task(ex.code)} style={{ textDecoration: 'none', minWidth: 0 }}>
        <div
            style={{
                border: accent ? `1px solid ${accent}44` : hair,
                borderRadius: 14,
                padding: '13px 14px',
                background: compact ? c.cardIn : c.card,
                minWidth: 0,
            }}
        >
            <Row style={{ gap: 10 }}>
                <Icon k={ex.track} size={14} color={c.muted} />
                <span
                    style={{
                        fontSize: 13,
                        color: c.textPri,
                        fontWeight: 400,
                        flex: 1,
                        minWidth: 0,
                        letterSpacing: '-0.01em',
                    }}
                >
                    {ex.name}
                </span>
                <Icon k="chev" size={12} color={c.subtle} />
            </Row>
            <Meta style={{ fontSize: 9, letterSpacing: '0.08em', display: 'block', marginTop: 8 }}>
                {ex.min ? t('app.task.min', { n: ex.min }) : t('app.task.rule')} ·{' '}
                {t(LOAD[ex.load] || ex.load)} · {patternName}
            </Meta>
            <XpTag ex={ex} content={content} scoreOf={scoreOf} style={{ marginTop: 9 }} />
            </div>
        </Link>
    );
};

/** The single pattern doing the most damage right now. */
const TopBlocker: React.FC<{ p: PatState }> = ({ p }) => {
    const t = useT();
    const pct = p.pct as number;
    const pz = patternZone(pct, p.pol);

    return (
        <Link to={R.dir(p.dim)} style={{ textDecoration: 'none' }}>
            <Col gap={11} style={{ minWidth: 0 }}>
                <Row style={{ gap: 9 }}>
                    <span
                        style={{
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            background: pz.hex,
                            boxShadow: `0 0 7px ${pz.hex}`,
                        }}
                    />
                    <span style={{ fontSize: 14, color: c.textPri, fontWeight: 400, flex: 1, minWidth: 0 }}>
                        {p.name}
                    </span>
                    <span style={{ fontFamily: f.mono, fontSize: 14, color: c.textPri }}>{Math.round(pct)}</span>
                </Row>
                <SegBarC pct={effectiveStrength(pct, p.pol)} hex={pz.hex} n={12} />
                <Meta style={{ fontSize: 9, letterSpacing: '0.1em', color: pz.hex }}>{t(strengthKey(pct, p.pol))}</Meta>
                <Muted style={{ fontSize: 12 }}>{p.sub}</Muted>
                <Row style={{ gap: 7 }}>
                    <Meta style={{ fontSize: 9.5, color: pz.hex, letterSpacing: '0.12em' }}>
                        {t('app.state.openDirection')}
                    </Meta>
                    <Icon k="arrow" size={12} color={pz.hex} />
                </Row>
            </Col>
        </Link>
    );
};

/**
 * Seven-day points. Rendered flat and labelled honestly while empty.
 *
 * Bars are always ACT — points are a currency, not a score, so they must not
 * inherit the index's zone colour. Tinting them by zone washed today's bar into
 * a muddy 30%-alpha olive whenever the goal wasn't reached yet.
 */
export const XPWeek: React.FC<{
    data: number[];
    goal: number;
    level: number;
    toNext: number;
    /** 0–1 through the current level. */
    levelPct: number;
}> = ({ data, goal, level, toNext, levelPct }) => {
    const { t, tn } = useTt();
    const H = 92;
    const max = Math.max(goal, ...data) * 1.14 || 1;
    const sum = data.reduce((a, b) => a + b, 0);
    const hit = data.filter((v) => v >= goal).length;
    // The week runs Monday-first, so today is at `todayIndex()` — not in the
    // last slot. Marking the last one put the highlight on Sunday all week.
    const bars = data.map((v, i) => ({ id: `xp-${i}`, v, today: i === todayIndex(), reached: v >= goal }));

    const numColor = (v: number, today: boolean): string => {
        if (v === 0) return c.subtle;
        return today ? c.textPri : c.muted;
    };

    return (
        <div>
            <div style={{ position: 'relative', height: H, marginTop: 4 }}>
                <div
                    style={{
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        bottom: `${(goal / max) * 100}%`,
                        borderTop: '1px dashed rgba(232,230,224,0.16)',
                    }}
                />
                <div
                    style={{
                        position: 'absolute',
                        left: 0,
                        bottom: `${(goal / max) * 100}%`,
                        transform: 'translateY(-3px)',
                        background: c.card,
                        paddingRight: 5,
                    }}
                >
                    <Meta style={{ fontSize: 8.5, letterSpacing: '0.12em', color: c.subtle }}>
                        {t('app.state.goal', { goal })}
                    </Meta>
                </div>
                <Row style={{ position: 'absolute', inset: 0, gap: 7, alignItems: 'flex-end' }}>
                    {bars.map((b) => (
                        <Col
                            key={b.id}
                            style={{ flex: 1, alignItems: 'center', height: '100%', justifyContent: 'flex-end' }}
                            gap={5}
                        >
                            <span style={{ fontFamily: f.mono, fontSize: 9.5, color: numColor(b.v, b.today) }}>
                                {b.v || '–'}
                            </span>
                            {b.v === 0 ? (
                                <div
                                    style={{
                                        width: '100%',
                                        height: 5,
                                        borderRadius: 3,
                                        border: '1px dashed rgba(232,230,224,0.2)',
                                    }}
                                />
                            ) : (
                                <div
                                    style={{
                                        width: '100%',
                                        height: `${(b.v / max) * (H - 18)}px`,
                                        borderRadius: 5,
                                        background: b.today || b.reached ? ACT : `${ACT}66`,
                                        boxShadow: b.today ? `0 0 10px ${ACT}66` : 'none',
                                    }}
                                />
                            )}
                        </Col>
                    ))}
                </Row>
            </div>
            <Row style={{ gap: 7, marginTop: 7 }}>
                {DAY_KEYS.map((key, i) => {
                    const today = i === todayIndex();

                    return (
                        <Col key={key} style={{ flex: 1, alignItems: 'center' }}>
                            <span
                                style={{
                                    fontFamily: f.mono,
                                    fontSize: 9,
                                    letterSpacing: '0.06em',
                                    color: today ? c.textPri : c.subtle,
                                    background: today ? 'rgba(232,230,224,0.08)' : 'transparent',
                                    borderRadius: 99,
                                    padding: today ? '2px 7px' : '2px 0',
                                }}
                            >
                                {t(key)}
                            </span>
                        </Col>
                    );
                })}
            </Row>
            <Row style={{ marginTop: 14, paddingTop: 13, borderTop: hair2, gap: 14, alignItems: 'flex-end' }}>
                <Col gap={4}>
                    <Row style={{ gap: 6 }}>
                        <span
                            style={{
                                fontFamily: f.mono,
                                fontSize: 17,
                                fontWeight: 300,
                                letterSpacing: '-0.03em',
                                color: c.textPri,
                            }}
                        >
                            {sum}
                        </span>
                        <Meta style={{ fontSize: 9.5, color: c.muted }}>{t('app.state.pointsWeek')}</Meta>
                    </Row>
                    <Meta style={{ fontSize: 9, color: c.subtle, letterSpacing: '0.1em' }}>
                        {tn('app.state.daysOnGoal', hit)}
                    </Meta>
                </Col>
                <Col style={{ flex: 1, alignItems: 'flex-end', minWidth: 0 }} gap={6}>
                    <Hint text={t('app.state.level.hint')} align="right">
                        <Meta style={{ fontSize: 9.5, color: c.muted, cursor: 'help' }}>
                            {t('app.state.levelNext', { level, next: level + 1 })}
                        </Meta>
                    </Hint>
                    <Row style={{ gap: 8, width: '100%' }}>
                        <div style={{ flex: 1, height: 4, borderRadius: 2, background: c.track, overflow: 'hidden' }}>
                            <div
                                style={{
                                    width: `${Math.round(levelPct * 100)}%`,
                                    height: '100%',
                                    background: ACT,
                                    boxShadow: `0 0 6px ${ACT}55`,
                                    transition: 'width 420ms ease',
                                }}
                            />
                        </div>
                        <Meta style={{ fontSize: 9, color: c.subtle, letterSpacing: '0.08em' }}>
                            {tn('app.state.points', toNext)}
                        </Meta>
                    </Row>
                </Col>
            </Row>
        </div>
    );
};

const EmptyWeek: React.FC = () => {
    const t = useT();

    return (
        <>
            <Row style={{ gap: 7, alignItems: 'flex-end', opacity: 0.5 }}>
                {DAY_KEYS.map((key, i) => (
                    <Col key={key} style={{ flex: 1, alignItems: 'center' }} gap={6}>
                    <div style={{ width: '100%', height: 46, display: 'flex', alignItems: 'flex-end' }}>
                        <div
                            style={{
                                width: '100%',
                                height: i === todayIndex() ? 6 : 3,
                                borderRadius: 3,
                                background: 'rgba(232,230,224,0.08)',
                            }}
                        />
                    </div>
                        <span style={{ fontFamily: f.mono, fontSize: 9, color: c.subtle }}>{t(key)}</span>
                    </Col>
                ))}
            </Row>
            <Muted style={{ fontSize: 12, marginTop: 12 }}>{t('app.state.week.empty')}</Muted>
        </>
    );
};

/** Picks up to three exercises for the strongest blockers the user actually has. */
function suggestions(profile: Profile, content: AppContent): Array<{ ex: Exercise; pattern: PatState }> {
    // Ranked by the inverted value: "needs work most" covers both a strong
    // blocker and a barely-there engine, which is what the colour scale says too.
    const ranked = profile.patterns
        .filter((p) => p.pct != null)
        .sort((a, b) => effectiveStrength(b.pct as number, b.pol) - effectiveStrength(a.pct as number, a.pol));
    const out: Array<{ ex: Exercise; pattern: PatState }> = [];
    const seen = new Set<string>();
    let budget = 16; // ~15 minutes a day is the ceiling, not a task count.

    ranked.forEach((p) => {
        if (out.length >= 3) return;
        const list = (content.exsByPattern[p.code] || []).slice().sort((a, b) => (a.min ?? 5) - (b.min ?? 5));
        const pick = list.find((e) => !seen.has(e.code) && (e.min ?? 5) <= budget);

        if (!pick) return;
        seen.add(pick.code);
        budget -= pick.min ?? 5;
        out.push({ ex: pick, pattern: p });
    });
    return out;
}

/* ── screen ────────────────────────────────────────── */

const StatePage: React.FC = () => {
    const { user, userProfile, loading: authLoading } = useAuth();
    const { lang } = useLang();
    const { t, tn } = useTt();
    const navigate = useNavigate();
    const { content, error } = useAppContent();
    const profile = useProfile(content, user?.id, lang);
    const desktop = useIsDesktop(DESKTOP);
    const [howHidden, setHowHidden] = React.useState(() => {
        try {
            return localStorage.getItem(HOW_KEY) === '1';
        } catch {
            return false;
        }
    });

    const dismissHow = React.useCallback(() => {
        setHowHidden(true);
        try {
            localStorage.setItem(HOW_KEY, '1');
        } catch {
            /* private mode — the block simply reappears next session */
        }
    }, []);

    const scoreOf = React.useCallback(
        (code: string) => profile.directions.find((d) => d.code === code)?.score ?? null,
        [profile.directions],
    );

    if (error) {
        return (
            <AppShell active="state" title={t('app.tab.state')}>
                <Col style={{ padding: 20, maxWidth: 420 }} gap={12}>
                    <Body>{t('app.error.content')}</Body>
                    <Btn onClick={() => window.location.reload()}>{t('app.action.retry')}</Btn>
                </Col>
            </AppShell>
        );
    }

    const busy = !content || profile.loading || authLoading;
    const z = scoreColor(profile.index);
    const empty = profile.maturity === 'empty';
    const hasReports = profile.reports > 0;
    const showHow = !empty && !hasReports && !howHidden;

    // Insight source: the weakest measured direction has the most to say; with
    // nothing measured we fall back to the library and say so on the card.
    const weakest = [...profile.directions].filter((d) => d.deep).sort((a, b) => (a.score ?? 0) - (b.score ?? 0))[0];
    const insightDir = weakest?.code || 'cognition';

    const picks = content && !empty ? suggestions(profile, content) : [];
    const totalMin = picks.reduce((a, p) => a + (p.ex.min ?? 0), 0);
    const topBlocker = profile.patterns
        .filter((p) => p.pct != null)
        .sort((a, b) => effectiveStrength(b.pct as number, b.pol) - effectiveStrength(a.pct as number, a.pol))[0];
    // The top of that ranking can legitimately be a stalled engine, so the panel
    // says which it is instead of always claiming "blocker".
    const topTitle = t(topBlocker?.pol === 'engine' ? 'app.state.weakestDriver' : 'app.state.topBlocker');

    const shellProps = {
        active: 'state' as const,
        title: t('app.tab.state'),
        subtitle: empty
            ? t('app.state.notTested')
            : t('app.state.indexZone', { index: String(profile.index), zone: t(z.key) }),
        index: profile.index,
        email: userProfile?.email || user?.email,
        tasksDone: 0,
        tasksTotal: picks.length,
        chips: <HeaderStatus streak={profile.streak} />,
        mobileHeader: !desktop ? <MobileHeader streak={profile.streak} index={profile.index} /> : undefined,
    };

    if (busy) {
        return (
            <AppShell {...shellProps}>
                <Col style={{ padding: '60px 20px', alignItems: 'center' }} gap={14}>
                    <Orb empty size={180} />
                    <Meta style={{ fontSize: 10, color: c.subtle }}>{t('app.state.loading')}</Meta>
                </Col>
            </AppShell>
        );
    }

    /* ── before the test ─────────────────────────────── */
    const emptyState = (
        <>
            <Col style={{ padding: '26px 26px 0', alignItems: 'center' }} gap={12}>
                <div
                    style={{
                        fontFamily: f.serif,
                        fontSize: 25,
                        fontWeight: 300,
                        lineHeight: 1.2,
                        letterSpacing: '-0.02em',
                        color: c.textPri,
                        textAlign: 'center',
                    }}
                >
                    {t('app.state.empty.title')}
                </div>
                <Muted style={{ textAlign: 'center', fontSize: 13.5, maxWidth: 430 }}>
                    {t('app.state.empty.body')}
                </Muted>
            </Col>
            <div style={{ padding: '22px 20px 0', maxWidth: 380, margin: '0 auto' }}>
                <Btn primary ic="arrow" onClick={() => navigate(QUIZ.full)}>
                    {t('app.state.empty.cta')}
                </Btn>
                <Row style={{ justifyContent: 'center', gap: 14, marginTop: 14 }}>
                    <Meta style={{ fontSize: 10 }}>{t('app.state.empty.meta.time')}</Meta>
                    <Meta style={{ fontSize: 10, color: c.subtle }}>·</Meta>
                    <Meta style={{ fontSize: 10 }}>{t('app.state.empty.meta.pause')}</Meta>
                </Row>
            </div>
        </>
    );

    const weekPanelRight = hasReports ? (
        <Meta style={{ fontSize: 9.5 }}>
            {t('app.state.average')}{' '}
            <span style={{ color: c.textPri }}>{Math.round(profile.week.reduce((a, b) => a + b, 0) / 7)}</span>
        </Meta>
    ) : (
        <Meta style={{ fontSize: 9.5, color: c.subtle }}>{t('app.pattern.collecting')}</Meta>
    );

    /* ── desktop: three columns ──────────────────────── */
    if (desktop) {
        return (
            <AppShell {...shellProps}>
                <Appear>
                    <DeskRow>
                        <Rail width={340}>
                            <Panel pad="24px 22px 20px">
                                <Orb score={profile.index} size={218} empty={empty} />
                                {/* The zone ladder first — where the measured index
                                    sits; the tasks points below are the second scale. */}
                                {!empty && profile.index != null && (
                                    <div style={{ marginTop: 18, marginLeft: -20, marginRight: -20 }}>
                                        <ZoneLevels score={profile.index} />
                                    </div>
                                )}
                                {!empty && (
                                    <Honesty
                                        base={profile.base}
                                        dynamic={profile.dynamic}
                                        level={profile.level}
                                        levelPct={profile.levelPct}
                                        testedAt={profile.testedAt}
                                        style={{ marginTop: 20 }}
                                    />
                                )}
                            </Panel>

                            {!empty && (
                                <Panel title={t('app.state.week7')} right={weekPanelRight}>
                                    {hasReports ? (
                                        <XPWeek
                                            data={profile.week}
                                            goal={DAY_GOAL}
                                            level={profile.level}
                                            toNext={profile.xpToNext}
                                            levelPct={profile.levelPct}
                                        />
                                    ) : (
                                        <EmptyWeek />
                                    )}
                                </Panel>
                            )}
                        </Rail>

                        <Fluid>
                            {empty && <Panel pad="10px 22px 30px">{emptyState}</Panel>}

                            {showHow && (
                                <Panel title={t('app.state.how')} pad="16px 22px 18px">
                                    <HowItWorks onClose={dismissHow} bare />
                                </Panel>
                            )}

                            {content && (
                                <Insight content={content} dirCode={insightDir} fromLibrary={!hasReports} wide />
                            )}

                            {!empty && content && (
                                <Panel
                                    title={t('app.state.directions')}
                                    right={
                                        <Link to={R.dirs} style={{ textDecoration: 'none' }}>
                                            <Meta style={{ fontSize: 9.5 }}>
                                                {t('app.state.directions.taken', {
                                                    n: profile.measuredTracks.length,
                                                })}
                                            </Meta>
                                        </Link>
                                    }
                                    pad="18px 22px 20px"
                                >
                                    <DirGrid dirs={profile.directions} content={content} />
                                    {profile.measuredTracks.length < 8 && (
                                        <Muted style={{ fontSize: 12, marginTop: 14 }}>
                                            {t('app.state.directions.lockedNote')}
                                        </Muted>
                                    )}
                                </Panel>
                            )}
                        </Fluid>

                        {!empty && content && (
                            <Rail width={320}>
                                <Panel
                                    title={t('app.state.today')}
                                    right={
                                        <Meta style={{ fontSize: 9.5 }}>
                                            ≈{t('app.task.min', { n: totalMin })}
                                        </Meta>
                                    }
                                    pad="14px 16px 16px"
                                >
                                    {picks.length ? (
                                        <>
                                            <Col gap={9}>
                                                {picks.map(({ ex, pattern }, i) => (
                                                    <SuggestionRow
                                                        key={ex.code}
                                                        ex={ex}
                                                        patternName={pattern.name}
                                                        content={content}
                                                        scoreOf={scoreOf}
                                                        accent={
                                                            i === 0
                                                                ? patternZone(pattern.pct as number, pattern.pol).hex
                                                                : undefined
                                                        }
                                                        compact
                                                    />
                                                ))}
                                            </Col>
                                            <Muted style={{ fontSize: 11.5, marginTop: 12 }}>
                                                {t('app.state.today.note')}
                                            </Muted>
                                        </>
                                    ) : (
                                        <Muted style={{ fontSize: 12.5 }}>{t('app.state.today.none')}</Muted>
                                    )}
                                </Panel>

                                {topBlocker && (
                                    <Panel title={topTitle} pad="16px 18px 18px">
                                        <TopBlocker p={topBlocker} />
                                    </Panel>
                                )}
                            </Rail>
                        )}
                    </DeskRow>
                </Appear>
            </AppShell>
        );
    }

    /* ── mobile: one column ──────────────────────────── */
    return (
        <AppShell {...shellProps}>
            <Appear>
                <div style={{ marginTop: 8 }}>
                    <Orb score={profile.index} size={214} empty={empty} />
                </div>
            </Appear>

            {empty ? (
                <Appear delay={70}>{emptyState}</Appear>
            ) : (
                <Appear delay={70}>
                    {/* Zone ladder first, tasks points second — same reading
                        order as the direction screen. */}
                    {profile.index != null && <ZoneLevels score={profile.index} style={{ marginTop: 16 }} />}
                    <Honesty
                        base={profile.base}
                        dynamic={profile.dynamic}
                        level={profile.level}
                        levelPct={profile.levelPct}
                        testedAt={profile.testedAt}
                        style={{ margin: '16px 20px 0' }}
                    />
                </Appear>
            )}

            {showHow && (
                <Appear delay={110}>
                    <div style={{ margin: '14px 20px 0' }}>
                        <HowItWorks onClose={dismissHow} />
                    </div>
                </Appear>
            )}

            <Appear delay={150}>
                <div style={{ padding: '20px 20px 0' }}>
                    {content && <Insight content={content} dirCode={insightDir} fromLibrary={!hasReports} />}
                </div>
            </Appear>

            {empty && (
                <Muted style={{ fontSize: 12, padding: '16px 22px 0', textAlign: 'center' }}>
                    {t('app.state.libraryNote')}
                </Muted>
            )}

            {!empty && content && (
                <Appear delay={190}>
                    <Sec
                        title={t('app.state.directions')}
                        right={
                            <Link to={R.dirs} style={{ textDecoration: 'none' }}>
                                <Meta style={{ fontSize: 10 }}>{t('app.state.directions.all')}</Meta>
                            </Link>
                        }
                    >
                        <DirStrip dirs={profile.directions} content={content} />
                        {profile.measuredTracks.length < 8 && (
                            <Muted style={{ fontSize: 12, marginTop: 12 }}>
                                {t('app.state.directions.lockedNote')}
                            </Muted>
                        )}
                    </Sec>
                </Appear>
            )}

            {!empty && content && picks.length > 0 && (
                <Appear delay={230}>
                    <Sec
                        title={t('app.state.today')}
                        right={<Meta style={{ fontSize: 10 }}>≈{t('app.task.min', { n: totalMin })}</Meta>}
                    >
                        <Col gap={9}>
                            {picks.map(({ ex, pattern }, i) => (
                                <SuggestionRow
                                    key={ex.code}
                                    ex={ex}
                                    patternName={pattern.name}
                                    content={content}
                                    scoreOf={scoreOf}
                                    accent={i === 0 ? patternZone(pattern.pct as number, pattern.pol).hex : undefined}
                                />
                            ))}
                        </Col>
                        <Muted style={{ fontSize: 12, marginTop: 12 }}>{t('app.state.today.note')}</Muted>
                    </Sec>
                </Appear>
            )}

            {!empty && topBlocker && (
                <Appear delay={250}>
                    <Sec title={topTitle}>
                        <div style={{ background: c.card, border: hair, borderRadius: 16, padding: '15px 16px' }}>
                            <TopBlocker p={topBlocker} />
                        </div>
                    </Sec>
                </Appear>
            )}

            {!empty && (
                <Appear delay={270}>
                    <Sec
                        title={t('app.state.week7')}
                        right={
                            hasReports ? (
                                <Meta style={{ fontSize: 10 }}>
                                    {t('app.state.average')}{' '}
                                    <span style={{ color: c.textPri }}>
                                        {tn(
                                            'app.state.points',
                                            Math.round(profile.week.reduce((a, b) => a + b, 0) / 7),
                                        )}
                                    </span>
                                </Meta>
                            ) : (
                                <Meta style={{ fontSize: 10, color: c.subtle }}>{t('app.pattern.collecting')}</Meta>
                            )
                        }
                        style={{ padding: '20px 20px 26px' }}
                    >
                        {hasReports ? (
                            <XPWeek
                                data={profile.week}
                                goal={DAY_GOAL}
                                level={profile.level}
                                toNext={profile.xpToNext}
                                levelPct={profile.levelPct}
                            />
                        ) : (
                            <EmptyWeek />
                        )}
                    </Sec>
                </Appear>
            )}
        </AppShell>
    );
};

export default StatePage;
